import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Pagination from '../components/shared/Pagination';
import { TableSkeleton } from '../components/shared/LoaderSkeleton';

function InvoicesIndex() {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        total_pages: 1,
        total_count: 0,
        current_page: 1,
        per_page: 25
    });

    useEffect(() => {
        loadInvoices();
    }, [search, currentPage]);

    const loadInvoices = async () => {
        try {
            setLoading(true);
            const response = await api.getInvoices({ q: search, page: currentPage });
            if (response.success) {
                setInvoices(Array.isArray(response.data) ? response.data : (response.data.invoices || []));
                if (response.meta) {
                    setPagination({
                        total_pages: response.meta.total_pages || 1,
                        total_count: response.meta.total_count || 0,
                        current_page: response.meta.current_page || 1,
                        per_page: response.meta.per_page || 25
                    });
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading && invoices.length === 0) return <TableSkeleton />;

    return (
        <div className="mt-16">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Invoices</h1>
                    <p className="text-slate-500 mt-0.5 text-sm">
                        {pagination.total_count} invoices found
                    </p>
                </div>
                <Link to="/invoices/new" className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 shadow-sm transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                    New Invoice
                </Link>
            </div>

            <div className="mb-6">
                <div className="relative">
                    <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search invoices..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-sm transition-all"
                    />
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="text-left py-3 px-4 font-semibold text-slate-700">Invoice #</th>
                                <th className="text-left py-3 px-4 font-semibold text-slate-700">Customer</th>
                                <th className="text-left py-3 px-4 font-semibold text-slate-700">Date</th>
                                <th className="text-right py-3 px-4 font-semibold text-slate-700">Amount</th>
                                <th className="text-right py-3 px-4 font-semibold text-slate-700">Due</th>
                                <th className="text-center py-3 px-4 font-semibold text-slate-700">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {invoices.map((invoice: any) => (
                                <tr key={invoice.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="py-3.5 px-4 font-medium text-indigo-600">
                                        <Link to={`/invoices/${invoice.id}`} className="hover:text-indigo-800">
                                            {invoice.invoice_number}
                                        </Link>
                                    </td>
                                    <td className="py-3.5 px-4 text-slate-700 font-medium">{invoice.customer_name}</td>
                                    <td className="py-3.5 px-4 text-slate-600">
                                        <div className="flex flex-col">
                                            <span>{new Date(invoice.created_at || invoice.invoice_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                            <span className="text-[11px] text-slate-400 capitalize">{new Date(invoice.created_at || invoice.invoice_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
                                        </div>
                                    </td>
                                    <td className="py-3.5 px-4 text-right font-semibold text-slate-900">₹{invoice.total_amount?.toLocaleString()}</td>
                                    <td className="py-3.5 px-4 text-right text-slate-600">₹{invoice.balance_due?.toLocaleString()}</td>
                                    <td className="py-3.5 px-4 text-center">
                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${invoice.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                                            invoice.status === 'partial' ? 'bg-amber-50 text-amber-700 border border-amber-100' :
                                                'bg-slate-50 text-slate-700 border border-slate-100'
                                            }`}>
                                            {invoice.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {invoices.length === 0 && !loading && (
                    <div className="py-12 text-center text-slate-500">
                        No invoices found.
                    </div>
                )}
            </div>

            <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                totalCount={pagination.total_count}
                perPage={pagination.per_page}
                onPageChange={handlePageChange}
            />
        </div>
    );
}

export default InvoicesIndex;
